import { useState, useRef, useEffect } from 'react';
import { useConversation } from '@elevenlabs/react';

export enum LoadingState {
  INITIAL = 'initial',
  CONNECTING = 'connecting',
  READY = 'ready',
  ACTIVE = 'active',
  ENDING = 'ending',
  ERROR = 'error',
}

export interface Transcript {
  source: string;
  message: string;
  timestamp: Date;
}

interface UseSompoAgentCallProps {
  agentId: string;
  voiceId: string;
  firstMessage: string;
  systemPrompt: string;
  maxCallDuration: number;
}

export const useSompoAgentCall = ({
  agentId,
  voiceId,
  firstMessage,
  systemPrompt,
  maxCallDuration,
}: UseSompoAgentCallProps) => {
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.INITIAL);
  const [error, setError] = useState<string>('');
  const [transcript, setTranscript] = useState<Transcript[]>([]);
  const [isCallActive, setIsCallActive] = useState(false);
  const [callDuration, setCallDuration] = useState(0);

  const startTimeRef = useRef<number>(0);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ElevenLabs conversation hook
  const conversation = useConversation({
    onConnect: () => {
      console.log('Connected to ElevenLabs');
      setLoadingState(LoadingState.READY);
      setIsCallActive(true);

      // Start call timer
      startTimeRef.current = Date.now();
      callTimerRef.current = setInterval(() => {
        setCallDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    },
    onDisconnect: () => {
      console.log('Disconnected from ElevenLabs');
      setIsCallActive(false);
      setLoadingState(LoadingState.INITIAL);

      // Clear timer
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
    },
    onMessage: (message: any) => {
      console.log('Received message:', message);
      // Add messages to transcript - handling both user and agent messages
      try {
        // Extract the actual message content
        let content = '';
        let source = 'agent';

        if (typeof message === 'string') {
          content = message;
        } else if (message?.message) {
          // Handle structured messages
          if (typeof message.message === 'string') {
            content = message.message;
          } else if (message.message?.content) {
            content = message.message.content;
            source = message.message.role === 'user' ? 'user' : 'agent';
          }
        } else if (message?.text) {
          content = message.text;
        } else if (message?.content) {
          content = message.content;
        }

        if (content) {
          const newEntry: Transcript = {
            source,
            message: content,
            timestamp: new Date(),
          };
          setTranscript(prev => [...prev, newEntry]);
        }
      } catch (err) {
        console.error('Error processing message:', err);
      }
    },
    onError: (error: any) => {
      console.error('ElevenLabs error:', error);
      setError('Connection error occurred');
      setLoadingState(LoadingState.ERROR);
    },
  });

  const handleAnswerCall = async () => {
    try {
      setLoadingState(LoadingState.CONNECTING);
      setError('');

      // Configure the conversation with proper Andrew Tan persona
      const prompt = {
        prompt: systemPrompt,
        firstMessage: firstMessage,
        model: "eleven_turbo_v2_5"
      };

      // Start the conversation with ElevenLabs
      await conversation.startSession({
        agentId: agentId,
        connectionType: 'webrtc',
        overrides: {
          agent: {
            prompt: prompt,
            firstMessage: firstMessage,
            language: "en"
          },
          tts: {
            voiceId: voiceId,
          }
        }
      });

    } catch (err) {
      console.error('Failed to start call:', err);
      setError('Failed to start the call. Please try again.');
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleEndCall = () => {
    setLoadingState(LoadingState.ENDING);
    conversation.endSession();
    setIsCallActive(false);
  };

  // Format time for display
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      if (isCallActive) {
        conversation.endSession();
      }
    };
  }, []);

  return {
    loadingState,
    error,
    transcript,
    isCallActive,
    callDuration,
    handleAnswerCall,
    handleEndCall,
    formatTime,
    isSpeaking: conversation.isSpeaking,
  };
};